import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { useAuth } from "../../context/AuthContext";
import { useModal } from "../../hooks/useModal";
import ChangePasswordModal from "./ChangePasswordModal";
import { fetchTenants, switchTenant } from "../../api/tenants";
import { queryKeys } from "../../lib/queryClient";

export default function UserDropdown() {
  const { user, signOut, isGlobalAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const passwordModal = useModal("change-password");
  const queryClient = useQueryClient();

  const tenantsQuery = useQuery({
    queryKey: queryKeys.tenants(),
    queryFn: fetchTenants,
    enabled: Boolean(user),
  });

  const tenants = tenantsQuery.data?.tenants ?? [];
  const currentTenantId = tenantsQuery.data?.currentTenantId ?? null;

  const switchTenantMutation = useMutation({
    mutationFn: (tenantId: string) => switchTenant(tenantId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.tenants() });
    },
  });

  const currentTenant = tenants.find((r) => r.id === currentTenantId);
  const displayName = user?.email?.split("@")[0] ?? "User";
  const initial = displayName.charAt(0).toUpperCase();

  async function handleSwitch(tenantId: string) {
    if (switching || tenantId === currentTenantId) return;
    setSwitching(true);
    try {
      await switchTenantMutation.mutateAsync(tenantId);
      setIsOpen(false);
      // 데이터가 tenant에 종속되므로 페이지 리로드
      window.location.reload();
    } catch {
      // 실패 시 유지
    } finally {
      setSwitching(false);
    }
  }

  return (
    <div className="relative">
      {/* 상단 띠는 전 화면에 늘 붙어 있어 가로 폭이 귀하다. 그래서 예전의 [아바타 + 이름 +
          매장명 + 화살표] 덩어리를 **동그란 아이콘 하나**로 줄였다 (2026-08-12).
          완전히 없애지는 않는다 — 로그아웃·비밀번호 변경·매장 전환이 이 메뉴에만 있어서,
          지우면 그 세 가지로 들어갈 길이 화면에서 사라진다. 이름·매장명은 눌러서 연 메뉴 안에 있다. */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        title={`${displayName}${currentTenant ? ` · ${currentTenant.name}` : ""} — 계정 메뉴`}
        aria-label="계정 메뉴 열기"
        className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-brand-500 text-base font-semibold text-white dropdown-toggle"
      >
        {initial}
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        className="absolute right-0 mt-[17px] flex w-[280px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark"
      >
        {/* 사용자 정보 */}
        <div className="pb-3 border-b border-gray-200 dark:border-gray-800">
          <span className="block font-medium text-gray-700 text-theme-sm dark:text-gray-400">
            {displayName}
          </span>
          <span className="mt-0.5 block text-theme-xs text-gray-500 dark:text-gray-400">
            {user?.email}
          </span>
          {/* 띠에서 뺀 매장명을 여기서 보여준다 — 어느 매장으로 보고 있는지는 계속 확인돼야 한다. */}
          {currentTenant && (
            <span className="mt-0.5 block text-theme-xs text-gray-400 dark:text-gray-500">
              {currentTenant.name}
            </span>
          )}
        </div>

        {/* 매장 전환 — 전역 관리자는 상단 띠 셀렉터로 일원화하므로 여기선 숨긴다(중복 방지). */}
        {tenants.length > 1 && !isGlobalAdmin() && (
          <div className="py-3 border-b border-gray-200 dark:border-gray-800">
            <span className="block px-3 mb-2 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              매장 전환
            </span>
            <ul className="flex flex-col gap-0.5">
              {tenants
                .filter((r) => r.status === "active")
                .map((r) => (
                  <li key={r.id}>
                    <button
                      onClick={() => handleSwitch(r.id)}
                      disabled={switching}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer disabled:opacity-50 ${
                        r.id === currentTenantId
                          ? "bg-blue-50 text-blue-700 font-semibold dark:bg-blue-500/10 dark:text-blue-300"
                          : "text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/5"
                      }`}
                    >
                      <span className="flex-1 text-left">{r.name}</span>
                      {r.id === currentTenantId && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300">
                          현재
                        </span>
                      )}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        )}

        {/* 비밀번호 변경 — 본인 계정 (티켓 #415) */}
        <button
          onClick={() => {
            setIsOpen(false);
            passwordModal.openModal();
          }}
          className="flex items-center gap-3 px-3 py-2 mt-3 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
        >
          <svg
            className="stroke-gray-500 group-hover:stroke-gray-700 dark:group-hover:stroke-gray-300"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="4.75"
              y="10.25"
              width="14.5"
              height="9"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M8.25 10V7.75C8.25 5.67893 9.92893 4 12 4C14.0711 4 15.75 5.67893 15.75 7.75V10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <circle cx="12" cy="14.75" r="1.25" fill="currentColor" />
          </svg>
          비밀번호 변경
        </button>

        {/* 로그아웃 */}
        <button
          onClick={() => {
            setIsOpen(false);
            signOut();
          }}
          className="flex items-center gap-3 px-3 py-2 mt-1 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
        >
          <svg
            className="fill-gray-500 group-hover:fill-gray-700 dark:group-hover:fill-gray-300"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M15.1007 19.247C14.6865 19.247 14.3507 18.9112 14.3507 18.497L14.3507 14.245H12.8507V18.497C12.8507 19.7396 13.8581 20.747 15.1007 20.747H18.5007C19.7434 20.747 20.7507 19.7396 20.7507 18.497L20.7507 5.49609C20.7507 4.25345 19.7433 3.24609 18.5007 3.24609H15.1007C13.8581 3.24609 12.8507 4.25345 12.8507 5.49609V9.74501L14.3507 9.74501V5.49609C14.3507 5.08188 14.6865 4.74609 15.1007 4.74609L18.5007 4.74609C18.9149 4.74609 19.2507 5.08188 19.2507 5.49609L19.2507 18.497C19.2507 18.9112 18.9149 19.247 18.5007 19.247H15.1007ZM3.25073 11.9984C3.25073 12.2144 3.34204 12.4091 3.48817 12.546L8.09483 17.1556C8.38763 17.4485 8.86251 17.4487 9.15549 17.1559C9.44848 16.8631 9.44863 16.3882 9.15583 16.0952L5.81116 12.7484L16.0007 12.7484C16.4149 12.7484 16.7507 12.4127 16.7507 11.9984C16.7507 11.5842 16.4149 11.2484 16.0007 11.2484L5.81528 11.2484L9.15585 7.90554C9.44864 7.61255 9.44847 7.13767 9.15547 6.84488C8.86248 6.55209 8.3876 6.55226 8.09481 6.84525L3.52309 11.4202C3.35673 11.5577 3.25073 11.7657 3.25073 11.9984Z"
              fill=""
            />
          </svg>
          로그아웃
        </button>
      </Dropdown>

      <ChangePasswordModal
        isOpen={passwordModal.isOpen}
        onClose={passwordModal.closeModal}
      />
    </div>
  );
}
